import { FeedbackFormData } from '../../components/feedback-form/feedback-types';
import { maxArrayLength } from '../../utils/validators/maxArrayLength';

export const schedulingFormData: FeedbackFormData = {
  id: 'scheduling',
  title: $localize`We'd like to hear some more`,
  subtitle: $localize`We promise it will only take a few minutes of your time, and your answers are of high value for us`,
  questions: [
    [
      {
        id: 'statements',
        title: $localize`To what extent do you agree with the following statements?`,
        type: 'radio-block',
        options: [
          { id: 'efficient', label: $localize`I always know what the most efficient order to operate my fields is` },
          {
            id: 'stressful-decision',
            label: $localize`It can be stressful to decide what the best order to operate my fields is`,
          },
          { id: 'stressful-reschedule', label: $localize`It can be stressful if I need to reschedule during the day ` },
          {
            id: 'necessary',
            label: $localize`It is necessary to create an efficient plan, so I will finish the field operations on time`,
          },
          { id: 'minimize-time', label: $localize`It is important to minimize the time we spend on the road ` },
        ],
      },
      {
        id: 'would-use',
        title: $localize`Why would or wouldn't you use a tool like this?`,
        subtitle: $localize`Please add a comment below`,
        type: 'textarea',
      },
    ],
    [
      {
        id: 'planning-timing',
        title: $localize`When do you plan which field to operate in which order at your farm`,
        subtitle: $localize`Select the answer that best fits your current practice`,
        type: 'radio',
        options: [
          { id: 'on-the-go', label: $localize`We plan on-the-go, when finishing one field we decide where to go next` },
          { id: 'morning', label: $localize`We plan the order of fields in the morning or the evening before` },
          { id: 'days', label: $localize`We plan 2-3 days in advance` },
          {
            id: 'week',
            label: $localize`We create a preliminary plan 1 week ahead, and adjust it as the operation date approaches`,
          },
        ],
      },
      {
        id: 'planning-method',
        title: $localize`How are you planning your field operations currently? `,
        subtitle: $localize`Select the answer that best fits your current practice`,
        type: 'radio',
        options: [
          { id: 'head', label: $localize`Planning occurs in my head` },
          { id: 'paper', label: $localize`I use paper or a whiteboard to do the planning` },
          { id: 'excel', label: $localize`I use an office application (Excel, Word or similar) for planning` },
          { id: 'system', label: $localize`I use a field management system for planning` },
          { id: 'not-me', label: $localize`I'm not the one planning at our farm` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: '',
        },
      },
    ],
    [
      {
        id: 'operations',
        title: $localize`For which operation types would you consider using a tool to find the most efficient order to operate your fields?`,
        subtitle: $localize`Select one or more answers`,
        type: 'check-block',
        options: [
          { id: 'none', label: $localize`None` },
          { id: 'tillage', label: $localize`Tillage` },
          { id: 'seeding', label: $localize`Seeding` },
          { id: 'spraying', label: $localize`Spraying` },
          { id: 'fertilizing', label: $localize`Fertilizing` },
          { id: 'slurry', label: $localize`Slurry application` },
          { id: 'harvesting', label: $localize`Harvesting` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: '',
        },
      },
      {
        id: 'users',
        title: $localize`Who is a scheduling tool relevant for, and how would they use it?`,
        type: 'radio-block',
        options: [
          { id: 'owner', label: $localize`Owner` },
          { id: 'manager', label: $localize`Manager` },
          { id: 'driver', label: $localize`Driver` },
          { id: 'advisor', label: $localize`Advisor` },
        ],
        values: [
          { id: 'creating', label: $localize`Creating the plan` },
          { id: 'viewing', label: $localize`Viewing the plan` },
          { id: 'both', label: $localize`Both creation and viewing` },
          { id: 'not-relevant', label: $localize`Not relevant` },
        ],
      },
    ],
    [
      {
        id: 'inputs',
        title: $localize`Which of the following inputs do you consider most essential for a scheduling tool to take into account?`,
        subtitle: $localize`Select up to 5 options that you consider the most important`,
        type: 'check-block',
        options: [
          { id: 'weather', label: $localize`Weather forecast` },
          { id: 'road-time', label: $localize`Transport time on road` },
          { id: 'idle-time', label: $localize`Idle time` },
          { id: 'fuel', label: $localize`Travel distance/fuel consumption` },
          { id: 'crop-type', label: $localize`Crop type` },
          { id: 'crop-conditions', label: $localize`Crop conditions` },
          { id: 'soil-type', label: $localize`Soil type` },
          { id: 'soil-moisture', label: $localize`Soil moisture` },
          { id: 'drivers', label: $localize`Available drivers` },
          { id: 'next-operation', label: $localize`Next operation at the field` },
          { id: 'unloading', label: $localize`Location for unloading or refilling material` },
          { id: 'field', label: $localize`Field entry/exit points` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: '',
        },
        validators: [maxArrayLength(5)],
      },
    ],
    [
      {
        id: 'output',
        title: $localize`How do you imagine using the output of this tool?`,
        type: 'radio',
        options: [
          { id: 'paper', label: $localize`Printed on paper` },
          { id: 'online', label: $localize`Online on a tablet or smartphone` },
          { id: 'terminal', label: $localize`In the machine terminal` },
        ],
        wildcard: {
          label: $localize`Other`,
          placeholder: '',
        },
      },
      {
        id: 'rescheduling',
        title: $localize`In case you need to reschedule, what would you expect to do if you had a tool like this? `,
        subtitle: $localize`Select the answer that best fits your expectations`,
        type: 'radio',
        options: [
          {
            id: 'on-the-go',
            label: $localize`I would decide where to go next on-the-go (continue without rescheduling)`,
          },
          {
            id: 'manual',
            label: $localize`I would manually select the remaining fields and use the tool for rescheduling`,
          },
          {
            id: 'preselected',
            label: $localize`I would expect that the remaining fields are preselected, and I would just have to reschedule`,
          },
          { id: 'automatic', label: $localize`I would expect the plan is always up to date according to my progress` },
        ],
      },
    ],
    [
      {
        id: 'pricing',
        title: $localize`At what price would you consider a product like this?`,
        subtitle: $localize`Please add a price in the four categories below, based on what you currently know about the idea - don't worry, you can't do anything wrong.`,
        type: 'pricing',
        options: [
          {
            id: 'too-low',
            label: $localize`At what price would you think the price is too low, that the product must be of bad quality? (Too cheap)`,
          },
          {
            id: 'good-value',
            label: $localize`At what price would you think the product is a bargain - a great buy for the money? (Cheap/Good value)`,
          },
          {
            id: 'expensive',
            label: $localize`At what price would you think the product is not out of the question, but you would have to give it some thoughts? (Expensive)`,
          },
          {
            id: 'too-expensive',
            label: $localize`At what price would you think the price is so expensive that you would not consider buying it? (Too expensive)`,
          },
        ],
      },
    ],
    [
      {
        id: 'anything-else',
        title: $localize`Anything else you'd like to share`,
        subtitle: $localize`Please add add any other comments, ideas and change requests, you would like to share with us`,
        type: 'textarea',
      },
      {
        id: 'email',
        title: $localize`E-mail contact`,
        subtitle: $localize`In case we need more information, we would like to contact you on the e-mail address you gave us when signing up.`,
        type: 'consent',
        options: [
          { id: 'mail', label: $localize`By ticking this box you allow us to get in contact with you by e-mail.` },
        ],
      },
    ],
  ],
};
